import axios, { type AxiosResponse } from 'axios'

import type { PaginatedResponse } from '@/api/schemas/common/pagination'
import type { ReportDictionaryItem } from '@/api/schemas/reports/common'
import type {
    DetailedReportsResponseSchema,
    ReportShiftSummariesResponseSchema,
    ReviewProcessCodeReportPayloadSchema,
    SimpleReportsResponseSchema,
    UpdateReportShiftSummaryPayloadSchema
} from '@/api/schemas/reports/review'
import { API_ENDPOINTS } from '@/constants/apiEndpoints'
import { REPORTS } from '@/constants/domain/reports'

function buildReviewUrl(baseUrl: string, idParamName: string, id: number | string, queryString = ''): string {
    return `${baseUrl}?${idParamName}=${id}${queryString ? `&${queryString.replace(/^\?/, '')}` : ''}`
}

class ReportReviewQueries {
    getReportsShiftSummaries(queryString = ''): Promise<AxiosResponse<ReportShiftSummariesResponseSchema>> {
        return axios.get<ReportShiftSummariesResponseSchema>(
            `${API_ENDPOINTS.REPORTS.REVIEW.REPORTS_SHIFT_SUMMARIES}${queryString}`
        )
    }

    generateReportsShiftSummariesReport(queryString = ''): Promise<AxiosResponse<ReportShiftSummariesResponseSchema>> {
        return axios.get<ReportShiftSummariesResponseSchema>(
            `${API_ENDPOINTS.REPORTS.REVIEW.REPORTS_SHIFT_SUMMARIES}?report=true${queryString}`,
            { timeout: 120000 }
        )
    }

    generateDetailedReportsShiftSummariesReport(queryString = ''): Promise<AxiosResponse<ReportShiftSummariesResponseSchema>> {
        return axios.get<ReportShiftSummariesResponseSchema>(
            `${API_ENDPOINTS.REPORTS.REVIEW.REPORTS_SHIFT_SUMMARIES}?detailedReport=true${queryString}`,
            { timeout: 120000 }
        )
    }

    updateReportShiftSummary(
        reportShiftSummaryId: number,
        payload: UpdateReportShiftSummaryPayloadSchema
    ): Promise<AxiosResponse<unknown>> {
        return axios.put<unknown>(`${API_ENDPOINTS.REPORTS.REVIEW.REPORTS_SHIFT_SUMMARIES}/${reportShiftSummaryId}`, payload)
    }

    getReportShiftSummary(reportShiftSummaryId: number | string): Promise<AxiosResponse<unknown>> {
        return axios.get<unknown>(`${API_ENDPOINTS.REPORTS.REVIEW.REPORTS_SHIFT_SUMMARIES}/${reportShiftSummaryId}`)
    }

    getDetailedReports(
        reportShiftSummaryId: number | string,
        queryString = ''
    ): Promise<AxiosResponse<DetailedReportsResponseSchema>> {
        return axios.get<DetailedReportsResponseSchema>(
            buildReviewUrl(
                API_ENDPOINTS.REPORTS.REVIEW.REPORTS_REVIEW,
                REPORTS.REVIEW_QUERY_PARAMS.REPORT_SHIFT_SUMMARY_ID,
                reportShiftSummaryId,
                queryString
            )
        )
    }

    getSimpleReports(queryString = ''): Promise<AxiosResponse<SimpleReportsResponseSchema>> {
        return axios.get<SimpleReportsResponseSchema>(`${API_ENDPOINTS.REPORTS.REVIEW.SIMPLE_REPORTS}${queryString}`)
    }

    getActiveReports(queryString = ''): Promise<AxiosResponse<SimpleReportsResponseSchema>> {
        return axios.get<SimpleReportsResponseSchema>(`${API_ENDPOINTS.REPORTS.REVIEW.ACTIVE_REPORTS}${queryString}`)
    }

    getReportStatuses(): Promise<AxiosResponse<PaginatedResponse<ReportDictionaryItem>>> {
        return axios.get<PaginatedResponse<ReportDictionaryItem>>(API_ENDPOINTS.REPORTS.REVIEW.REPORT_STATUSES)
    }

    getReportObjectTypes(): Promise<AxiosResponse<PaginatedResponse<ReportDictionaryItem>>> {
        return axios.get<PaginatedResponse<ReportDictionaryItem>>(API_ENDPOINTS.REPORTS.REVIEW.REPORT_OBJECT_TYPES)
    }

    getReportShiftSummaryStatuses(): Promise<AxiosResponse<PaginatedResponse<ReportDictionaryItem>>> {
        return axios.get<PaginatedResponse<ReportDictionaryItem>>('/reports/reports/dicts/report-shift-summary-statuses')
    }

    getUserDetailedReports(
        reportShiftSummaryId: number | string,
        queryString = ''
    ): Promise<AxiosResponse<DetailedReportsResponseSchema>> {
        return axios.get<DetailedReportsResponseSchema>(
            buildReviewUrl(
                API_ENDPOINTS.REPORTS.REVIEW.REPORTS,
                REPORTS.REVIEW_QUERY_PARAMS.REPORT_SHIFT_SUMMARY_ID,
                reportShiftSummaryId,
                queryString
            )
        )
    }

    reviewProcessCodeReport(payload: ReviewProcessCodeReportPayloadSchema): Promise<AxiosResponse<unknown>> {
        return axios.put<unknown>(`${API_ENDPOINTS.REPORTS.REVIEW.REVIEW_PROCESS_CODE_REPORT}/${payload.reportId}`, payload)
    }

    reviewShipmentProcessCodeReport(payload: ReviewProcessCodeReportPayloadSchema): Promise<AxiosResponse<unknown>> {
        return axios.put<unknown>(
            `${API_ENDPOINTS.SHIPMENTS.REPORTS.REVIEW_PROCESS_CODE_SHIPMENT_REPORT}/${payload.reportId}`,
            payload
        )
    }
}

export default new ReportReviewQueries()
